import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

import Button from '../common/Button';
import { supabase } from '../../config/supabase';
import { standardizeCardName } from '../../services/cardService';

interface TarotCard {
  id: string;
  card_name: string;
  orientation: string;
  meaning_summary: string | null;
  arcana_info: string | null;
  image_url: string | null;
  themes: string[] | null;
  shadow_themes: string[] | null;
  arcana_type: string | null;
  suit: string | null;
  traditional_imagery: string | null;
}

const CardExplorer: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isReversed, setIsReversed] = useState(false);
  const [currentCard, setCurrentCard] = useState<TarotCard | null>(null);
  const [showCardModal, setShowCardModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSearch = async () => {
    if (!searchTerm.trim()) return;

    setIsLoading(true);
    setError(null);

    try {
      const standardizedName = standardizeCardName(searchTerm);

      // First try exact match
      const { data: exactMatch, error: exactError } = await supabase
        .from('tarot_cards')
        .select('*')
        .eq('orientation', isReversed ? 'reversed' : 'upright')
        .eq('card_name', standardizedName)
        .limit(1);

      if (exactError) throw exactError;

      // If no exact match, try searching aliases
      if (!exactMatch || exactMatch.length === 0) {
        const { data: aliasMatch, error: aliasError } = await supabase
          .from('tarot_cards')
          .select('*')
          .eq('orientation', isReversed ? 'reversed' : 'upright')
          .contains('slug_aliases', [standardizedName.toLowerCase()])
          .limit(1);

        if (aliasError) throw aliasError;

        if (!aliasMatch || aliasMatch.length === 0) {
          throw new Error(`No card found matching "${searchTerm}". Please check the spelling and try again.`);
        }

        setCurrentCard(aliasMatch[0]);
      } else {
        setCurrentCard(exactMatch[0]);
      }

      setError(null);
    } catch (error) {
      console.error('Error searching for card:', error);
      setError(error instanceof Error ? error.message : 'Failed to find the card');
      setCurrentCard(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setIsReversed(false);
    setCurrentCard(null);
    setError(null);
  };

  const getArcanaDisplay = (card: TarotCard) => {
    if (!card.arcana_type) return 'Unknown Arcana';
    return card.arcana_type.toLowerCase() === 'major'
      ? 'Major Arcana'
      : `Minor Arcana${card.suit ? ` • ${card.suit}` : ''}`;
  };

  return (
    <div className="min-h-[calc(100vh-8rem)] bg-parchment dark:bg-ink">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="max-w-4xl mx-auto"
      >
        <div className="text-center mb-8">
          <h1 className="font-display text-3xl md:text-4xl font-semibold text-ink dark:text-parchment">
            Explore Card Meanings
          </h1>
          <div className="mt-3 text-lg text-ink dark:text-parchment/60 space-y-4">
            <p>Enter any tarot card name to discover its meanings, symbolism, and guidance.</p>
            <p>Or, <Link to="/virtual-deck" className="text-sun dark:text-ember hover:text-sun dark:hover:text-ember">explore a tarot deck virtually</Link> to become more familiar if you don't have a physical deck in front of you.</p>
          </div>
        </div>

        <div>
          <div className="flex flex-col md:flex-row gap-4 mb-8">
            <div className="flex-grow">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-mist text-sm">✧</span>
                </div>
                <input
                  type="text"
                  className="w-full pl-10 pr-10 py-2 border border-mist dark:border-dusk rounded-lg focus:outline-none focus:ring-2 focus:ring-sun/40 dark:focus:ring-ember/40 bg-parchment/60 dark:bg-shadow/60"
                  placeholder="Enter a card name (e.g., 'The Fool' or 'Queen of Cups')"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={handleKeyPress}
                />
                {searchTerm && (
                  <button
                    onClick={handleClearSearch}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-mist hover:text-ink/70 dark:hover:text-parchment/60 transition-colors"
                  >
                    <span className="text-sm">×</span>
                  </button>
                )}
              </div>
            </div>

            <div className="flex items-center gap-4">
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={isReversed}
                  onChange={() => setIsReversed(!isReversed)}
                  className="sr-only peer"
                />
                <div className="relative w-11 h-6 bg-mist peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-mist dark:peer-focus:ring-dusk rounded-full peer dark:bg-dusk peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-parchment after:border-mist after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-dusk peer-checked:bg-sun"></div>
                <span className="ms-2 text-sm font-medium text-ink/50 dark:text-parchment/60">Reversed</span>
              </label>

              <Button
                onClick={handleSearch}
                isLoading={isLoading}
              >
                Search
              </Button>
            </div>
          </div>

          {error ? (
            <div className="bg-sun/10 text-sun p-4 rounded-md">
              <span>{error}</span>
            </div>
          ) : isLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sun"></div>
              <p className="mt-4 text-ink/70 dark:text-parchment/60">Consulting the cards...</p>
            </div>
          ) : currentCard && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                {/* Card Image Column */}
                <div className="lg:col-span-1">
                  {currentCard.image_url ? (
                    <div className="flex flex-col items-center">
                      <button
                        onClick={() => setShowCardModal(true)}
                        className="w-48 h-80 bg-parchment dark:bg-shadow border-2 border-mist dark:border-dusk rounded-lg shadow-md flex items-center justify-center group relative overflow-hidden transform transition-all duration-300 hover:scale-[1.02] hover:shadow-lg"
                      >
                        <img
                          src={currentCard.image_url}
                          alt={currentCard.card_name}
                          className={`w-full h-full object-cover transition-transform duration-300 ${isReversed ? 'rotate-180' : ''}`}
                        />
                        <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </button>
                      <div className="mt-4 text-center">
                        <h2 className="text-2xl font-display font-bold text-ink dark:text-parchment">
                          {currentCard.card_name}
                        </h2>
                        <p className="text-sm text-sun dark:text-ember mt-1">
                          {getArcanaDisplay(currentCard)}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="w-48 h-80 bg-parchment dark:bg-shadow border-2 border-mist dark:border-dusk rounded-lg shadow-md flex items-center justify-center">
                      <span className="text-sun dark:text-ember font-serif text-lg">
                        {currentCard.card_name}
                      </span>
                    </div>
                  )}
                </div>

                {/* Interpretation Column */}
                <div className="lg:col-span-3 space-y-6">
                  {/* Card Meaning */}
                  <div className="bg-parchment/60 dark:bg-shadow/60 border border-mist dark:border-dusk rounded-xl overflow-hidden">
                    <div className="px-4 py-3 border-b border-mist dark:border-dusk">
                      <h3 className="text-xl font-display font-bold text-sun dark:text-ember">
                        Card Meaning
                      </h3>
                    </div>
                    <div className="p-6">
                      <div className="prose prose-gray dark:prose-invert max-w-none">
                        {currentCard.meaning_summary?.split('\n').map((paragraph, index) => (
                          <p key={index} className="text-ink dark:text-parchment/60 mb-4 last:mb-0">
                            {paragraph}
                          </p>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Arcana Information */}
                  {currentCard.arcana_info && (
                    <div className="bg-parchment/60 dark:bg-shadow/60 border border-mist dark:border-dusk rounded-xl overflow-hidden">
                      <div className="px-4 py-3 border-b border-mist dark:border-dusk">
                        <h3 className="text-xl font-display font-bold text-sun dark:text-ember">
                          Arcana Information
                        </h3>
                      </div>
                      <div className="p-6">
                        <div className="prose prose-gray dark:prose-invert max-w-none">
                          {currentCard.arcana_info.split('\n').map((paragraph, index) => (
                            <p key={index} className="text-ink dark:text-parchment/60 mb-4 last:mb-0">
                              {paragraph}
                            </p>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Themes */}
                  {currentCard.themes && currentCard.themes.length > 0 && (
                    <div className="bg-parchment/60 dark:bg-shadow/60 border border-mist dark:border-dusk rounded-xl overflow-hidden">
                      <div className="px-4 py-3 border-b border-mist dark:border-dusk">
                        <h3 className="text-xl font-display font-bold text-sun dark:text-ember">
                          Themes
                        </h3>
                      </div>
                      <div className="p-6">
                        <div className="flex flex-wrap gap-2">
                          {currentCard.themes.map((theme, index) => (
                            <span
                              key={index}
                              className="px-3 py-1 bg-vellum dark:bg-sun/30 text-sun dark:text-ember rounded-full text-sm"
                            >
                              {theme.replace(/[{}]/g, '')}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Shadow Themes */}
                  {currentCard.shadow_themes && currentCard.shadow_themes.length > 0 && (
                    <div className="bg-parchment/60 dark:bg-shadow/60 border border-mist dark:border-dusk rounded-xl overflow-hidden">
                      <div className="px-4 py-3 border-b border-mist dark:border-dusk">
                        <h3 className="text-xl font-display font-bold text-sun dark:text-ember">
                          Shadow Themes
                        </h3>
                      </div>
                      <div className="p-6">
                        <div className="flex flex-wrap gap-2">
                          {currentCard.shadow_themes.map((theme, index) => (
                            <span
                              key={index}
                              className="px-3 py-1 bg-vellum dark:bg-shadow/30 text-sun dark:text-ember rounded-full text-sm"
                            >
                              {theme.replace(/[{}]/g, '')}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* New Search Button */}
              <div className="mt-8 flex justify-center">
                <Button
                  variant="outline"
                  onClick={handleClearSearch}
                >
                  Search Another Card
                </Button>
              </div>
            </motion.div>
          )}

          <div className="mt-8 text-center space-x-4">
            <Link
              to="/guide/101"
              className="text-sun dark:text-ember hover:text-sun dark:hover:text-ember"
            >
              Tarot 101
            </Link>
            <span className="text-mist">•</span>
            <Link
              to="/virtual-deck"
              className="text-sun dark:text-ember hover:text-sun dark:hover:text-ember"
            >
              Virtual Deck Experience
            </Link>
            <span className="text-mist">•</span>
            <Link
              to="/faq"
              className="text-sun dark:text-ember hover:text-sun dark:hover:text-ember"
            >
              About This App
            </Link>
          </div>
        </div>

        {/* Card Modal */}
        {showCardModal && currentCard && (
          <div
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
            onClick={() => setShowCardModal(false)}
          >
            <div
              className="relative max-w-2xl w-full bg-parchment dark:bg-shadow rounded-lg shadow-xl overflow-y-auto max-h-[90vh]"
              onClick={e => e.stopPropagation()}
            >
              <button
                onClick={() => setShowCardModal(false)}
                className="absolute top-4 right-4 text-ink/50 hover:text-ink dark:text-parchment/60 dark:hover:text-parchment"
              >
                <span className="text-2xl leading-none">×</span>
              </button>

              <div className="p-6">
                {/* Card Image */}
                {currentCard.image_url && (
                  <div className="flex justify-center mb-6">
                    <img
                      src={currentCard.image_url}
                      alt={currentCard.card_name}
                      className={`max-h-[60vh] object-contain rounded-lg shadow-lg ${isReversed ? 'rotate-180' : ''}`}
                    />
                  </div>
                )}

                {/* Card Details */}
                <div className="space-y-6">
                  <div className="text-center">
                    <h2 className="text-3xl font-display font-bold text-ink dark:text-parchment">
                      {currentCard.card_name}
                    </h2>
                    <p className="text-lg text-sun dark:text-ember mt-2">
                      {getArcanaDisplay(currentCard)}
                      {isReversed && ' • Reversed'}
                    </p>
                  </div>

                  {/* Themes */}
                  {currentCard.themes && currentCard.themes.length > 0 && (
                    <div>
                      <h3 className="text-xl font-display font-bold text-sun dark:text-ember mb-3">
                        Themes
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {currentCard.themes.map((theme, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-vellum dark:bg-sun/30 text-sun dark:text-ember rounded-full text-sm"
                          >
                            {theme.replace(/[{}]/g, '')}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Shadow Themes */}
                  {currentCard.shadow_themes && currentCard.shadow_themes.length > 0 && (
                    <div>
                      <h3 className="text-xl font-display font-bold text-sun dark:text-ember mb-3">
                        Shadow Themes
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {currentCard.shadow_themes.map((theme, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-vellum dark:bg-shadow/30 text-sun dark:text-ember rounded-full text-sm"
                          >
                            {theme.replace(/[{}]/g, '')}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default CardExplorer;